import React, { useState, useEffect, useCallback } from 'react';
import { format, startOfWeek, addDays, addWeeks, isToday, isBefore, startOfDay } from 'date-fns';
import { ja } from 'date-fns/locale';
import Toast from './Toast';
import ConfirmModal from './ConfirmModal';
import Confetti from './Confetti';

const HabitTracker = () => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [habitData, setHabitData] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const habits = [
    { key: 'workout', label: '10分ワーク' },
    { key: 'stretch', label: '姿勢リセット' },
    { key: 'protein', label: 'タンパク質' },
    { key: 'water', label: '水分1.5L+' },
    { key: 'sleep', label: '睡眠7h' },
    { key: 'overeating', label: '夜食回避' }
  ];

  const cheers = ["その調子！✨", "Good!", "ナイス！🙌", "素晴らしい！", "Great job!", "やったね！🎉"];

  const dayLabels = ["日", "月", "火", "水", "木", "金", "土"];

  useEffect(() => {
    const savedData = localStorage.getItem('habitData');
    if (savedData) {
      try {
        setHabitData(JSON.parse(savedData));
      } catch (e) {
        console.error('データの読み込みに失敗しました:', e);
      }
    }
  }, []);

  const saveData = useCallback((key, value) => {
    const newData = { ...habitData, [key]: value };
    setHabitData(newData);
    try {
      localStorage.setItem('habitData', JSON.stringify(newData));
    } catch (e) {
      console.error('データの保存に失敗しました:', e);
    }
  }, [habitData]);

  const getCurrentWeekStart = () => {
    const today = new Date();
    const weekStart = startOfWeek(addWeeks(today, weekOffset), { weekStartsOn: 0 });
    return weekStart;
  };

  const getWeekDates = () => {
    const weekStart = getCurrentWeekStart();
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  };

  const formatDate = (date) => {
    return format(date, 'yyyy-MM-dd');
  };

  const displayToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  };

  const triggerConfetti = () => {
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 1200);
  };

  const handleHabitChange = (dateKey, habitKey, checked) => {
    const key = `${dateKey}_${habitKey}`;
    saveData(key, checked);
    
    if (checked) {
      displayToast(cheers[Math.floor(Math.random() * cheers.length)]);
      triggerConfetti();
    }

    // Reset completion status if any habit is unchecked
    const completionKey = `${dateKey}_completed`;
    if (habitData[completionKey]) {
      saveData(completionKey, false);
    }
  };

  const handleDetailChange = (key, value) => {
    saveData(key, value);
  };

  const handleCompleteDay = (dateKey) => {
    const completionKey = `${dateKey}_completed`;
    const checkedHabits = habits.filter(habit => 
      habitData[`${dateKey}_${habit.key}`]
    ).length;

    if (checkedHabits === 0) {
      displayToast("ちょっとだけ頑張ろう！💪");
      return;
    }

    const wasCompleted = habitData[completionKey];
    
    displayToast(
      `${habits.length}個中${checkedHabits}個 よくできました！<br><a href='https://www.youtube.com/@kanon_diet' target='_blank' style='color:#fff;text-decoration:underline;'>花音チャンネルで次のワークアウト ▶︎</a>`
    );

    if (!wasCompleted) {
      saveData(completionKey, true);
      triggerConfetti();
    }
  };

  const handleReset = () => {
    setShowConfirmModal(true);
  };

  const confirmReset = () => {
    localStorage.removeItem('habitData');
    setHabitData({});
    setShowConfirmModal(false);
    displayToast("データをリセットしました。");
  };

  const handlePrint = () => {
    window.print();
  };

  const goToToday = () => {
    setWeekOffset(0);
    setTimeout(() => {
      const todayRow = document.querySelector('.today-row');
      if (todayRow) {
        todayRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 200);
  };

  const getRowClass = (date) => {
    const today = startOfDay(new Date());
    const currentDate = startOfDay(date);
    
    if (isToday(date)) return 'today-row';
    if (isBefore(currentDate, today)) return 'past-row';
    return 'future-row';
  };

  const weekDates = getWeekDates();
  const weekStart = getCurrentWeekStart();
  const weekEnd = addDays(weekStart, 6);

  return (
    <div className="habit-tracker">
      <header className="header">
        <h1>1週間 習慣化ワークシート</h1>
        <p>チェックして "できた自分" を積み上げよう！</p>
      </header>

      <main className="main-content">
        <div className="week-nav">
          <div className="nav-buttons">
            <button onClick={() => setWeekOffset(weekOffset - 1)} className="nav-btn">
              前の週
            </button>
            <button onClick={goToToday} className="nav-btn today-btn">
              今日 ({format(new Date(), 'M/d')})
            </button>
            <button onClick={() => setWeekOffset(weekOffset + 1)} className="nav-btn">
              次の週
            </button>
          </div>
          <div className="week-display">
            {format(weekStart, 'yyyy/M/d', { locale: ja })} - {format(weekEnd, 'M/d', { locale: ja })}
          </div>
        </div>

        <div className="table-container">
          <div className="table-wrap">
            <table className="habit-table">
              <thead>
                <tr>
                  <th>日付</th>
                  {habits.map(habit => (
                    <th key={habit.key}>{habit.label}</th>
                  ))}
                  <th>完了</th>
                </tr>
              </thead>
              <tbody>
                {weekDates.map(date => {
                  const dateKey = formatDate(date);
                  const rowClass = getRowClass(date);
                  
                  return (
                    <React.Fragment key={dateKey}>
                      <tr className={rowClass} data-date={dateKey}>
                        <td className="day-label">
                          {format(date, 'M/d', { locale: ja })}({dayLabels[date.getDay()]})
                        </td>
                        {habits.map(habit => (
                          <td key={habit.key}>
                            <input
                              type="checkbox"
                              className="habit-checkbox"
                              checked={habitData[`${dateKey}_${habit.key}`] || false}
                              onChange={(e) => handleHabitChange(dateKey, habit.key, e.target.checked)}
                            />
                          </td>
                        ))}
                        <td>
                          <button
                            className={`complete-btn ${habitData[`${dateKey}_completed`] ? 'completed' : ''}`}
                            onClick={() => handleCompleteDay(dateKey)}
                          >
                            完了
                          </button>
                        </td>
                      </tr>
                      <tr className="details-row">
                        <td colSpan="8">
                          <details>
                            <summary className="details-summary">詳細入力</summary>
                            <div className="inputs-container">
                              <label>
                                体重(kg)<br />
                                <input
                                  type="number"
                                  step="0.1"
                                  placeholder="50.0"
                                  value={habitData[`${dateKey}_weight`] || ''}
                                  onChange={(e) => handleDetailChange(`${dateKey}_weight`, e.target.value)}
                                />
                              </label>
                              <label>
                                体脂肪(%)<br />
                                <input
                                  type="number"
                                  step="0.1"
                                  placeholder="25.0"
                                  value={habitData[`${dateKey}_fat`] || ''}
                                  onChange={(e) => handleDetailChange(`${dateKey}_fat`, e.target.value)}
                                />
                              </label>
                              <label>
                                睡眠(hh:mm)<br />
                                <input
                                  type="text"
                                  placeholder="7:30"
                                  value={habitData[`${dateKey}_sleep_time`] || ''}
                                  onChange={(e) => handleDetailChange(`${dateKey}_sleep_time`, e.target.value)}
                                />
                              </label>
                              <label>
                                気分<br />
                                <select
                                  value={habitData[`${dateKey}_mood`] || ''}
                                  onChange={(e) => handleDetailChange(`${dateKey}_mood`, e.target.value)}
                                >
                                  <option value=""></option>
                                  <option>最高</option>
                                  <option>良い</option>
                                  <option>普通</option>
                                  <option>悪い</option>
                                  <option>最悪</option>
                                </select>
                              </label>
                              <label>
                                生理<br />
                                <select
                                  value={habitData[`${dateKey}_cycle`] || ''}
                                  onChange={(e) => handleDetailChange(`${dateKey}_cycle`, e.target.value)}
                                >
                                  <option value=""></option>
                                  <option>生理中</option>
                                  <option>PMS</option>
                                  <option>排卵期</option>
                                  <option>通常</option>
                                </select>
                              </label>
                              <label style={{ flex: 1 }}>
                                メモ<br />
                                <textarea
                                  rows="2"
                                  value={habitData[`${dateKey}_memo`] || ''}
                                  onChange={(e) => handleDetailChange(`${dateKey}_memo`, e.target.value)}
                                />
                              </label>
                            </div>
                          </details>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="controls">
          <button className="btn" onClick={handleReset}>
            リセット
          </button>
          <button className="btn" onClick={handlePrint}>
            印刷 / PDF保存
          </button>
        </div>
      </main>

      <footer className="footer">
        <p>StyleUp Laboおすすめ「ほの花酵素」で内側からもキレイをサポート！</p>
        <a href="https://shop.styleup.jp/" target="_blank" rel="noopener noreferrer" className="cta-link">
          ほの花酵素をチェックする
        </a>
        <p style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
          ※ 本シートは一般的な健康情報であり医療行為ではありません。
        </p>
      </footer>

      <Toast show={showToast} message={toastMessage} />
      <ConfirmModal
        show={showConfirmModal}
        onConfirm={confirmReset}
        onCancel={() => setShowConfirmModal(false)}
        message="すべての入力をリセットしますか？"
      />
      <Confetti show={showConfetti} />
    </div>
  );
};

export default HabitTracker;